'use client';

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { Send, Sparkles, Wrench, Lock } from 'lucide-react';
import { api, ApiError, refreshSession, invalidateProfile, type AgentMessage, type ToolCall, type Usage } from '@/lib/api';
import { Link } from '@/i18n/routing';

// localStorage key + history cap: bounds both storage and the context we send
// to the model (cost control) — older turns silently age out.
export const CHAT_STORE_KEY = 'wow:chat:v1';
const MAX_TURNS = 30;

interface Turn extends AgentMessage {
  tools?: ToolCall[];
}

const toolLabel: Record<string, string> = {
  get_quote: '報價',
  get_candles: 'K線',
  get_news: '新聞',
};

export default function Chat() {
  const t = useTranslations('chat');
  const ta = useTranslations('auth');
  const locale = useLocale();
  const [turns, setTurns] = useState<Turn[]>([]);

  // Conversation survives tab switches / reloads: hydrate from localStorage on
  // mount, persist (capped) on every change. Cleared on logout.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(CHAT_STORE_KEY);
      if (saved) setTurns(JSON.parse(saved) as Turn[]);
    } catch {
      /* corrupt/absent → start fresh */
    }
  }, []);
  useEffect(() => {
    try {
      if (turns.length > 0) localStorage.setItem(CHAT_STORE_KEY, JSON.stringify(turns.slice(-MAX_TURNS)));
    } catch {
      /* storage full/unavailable → in-memory only */
    }
  }, [turns]);

  function clearChat() {
    setTurns([]);
    try {
      localStorage.removeItem(CHAT_STORE_KEY);
    } catch {
      /* ignore */
    }
  }
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [quota, setQuota] = useState(false); // 429: daily AI limit reached (not an error)
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const searchParams = useSearchParams();
  const initialQ = searchParams.get('q') ?? '';
  const sentQRef = useRef('');

  // Probe auth/quota on mount: /me/usage 401 → not logged in.
  useEffect(() => {
    api
      .usage()
      .then((u) => {
        setUsage(u);
        setAuthed(true);
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) setAuthed(false);
        else setAuthed(true); // network/other — don't block the UI
      });
  }, []);

  // Auto-send a question handed in from the header quick-ask (?q=...), once the
  // auth state is known and only once per distinct query.
  useEffect(() => {
    if (initialQ && authed !== null && sentQRef.current !== initialQ) {
      sentQRef.current = initialQ;
      send(initialQ);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQ, authed]);

  async function refreshUsage() {
    try {
      invalidateProfile(); // a message was just spent — force a fresh count
      setUsage(await api.usage());
    } catch {
      /* ignore */
    }
  }

  // Update the last turn (the streaming assistant bubble) in place.
  function updateLast(fn: (tn: Turn) => Turn) {
    setTurns((prev) => {
      if (prev.length === 0) return prev;
      const copy = [...prev];
      copy[copy.length - 1] = fn(copy[copy.length - 1]);
      return copy;
    });
  }

  function statusError(status: number): string {
    if (status === 401) {
      setAuthed(false);
      return t('needLogin');
    }
    if (status === 429) {
      setQuota(true); // render the distinct quota card, not a red error line
      return '';
    }
    if (status === 402) return t('upgrade');
    if (status === 503) return t('disabled');
    return `error ${status}`;
  }

  async function send(text: string) {
    const q = text.trim();
    if (!q || loading) return;
    if (authed === false) {
      setError(t('needLogin'));
      return;
    }
    setError('');
    setQuota(false);
    setInput('');
    const history: AgentMessage[] = [
      ...turns.slice(-MAX_TURNS).map((tn) => ({ role: tn.role, content: tn.content })),
      { role: 'user', content: q },
    ];
    // Optimistic user turn + empty assistant bubble (fills in as it streams).
    setTurns((prev) => [...prev, { role: 'user', content: q }, { role: 'assistant', content: '', tools: [] }]);
    setLoading(true);
    let streamed = false;
    try {
      const post = () =>
        fetch(`/api/v1/agent/chat/stream?locale=${encodeURIComponent(locale)}`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ messages: history }),
        });
      let res = await post();
      if (res.status === 401 && (await refreshSession())) {
        res = await post(); // access token expired mid-session — rotate + retry once
      }
      if (!res.ok || !res.body) {
        setTurns((prev) => prev.slice(0, -2)); // roll back both
        setInput(q);
        setError(statusError(res.status));
        return;
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() ?? '';
        for (const part of parts) {
          const line = part.split('\n').find((l) => l.startsWith('data:'));
          if (!line) continue;
          let ev: { type: string; text?: string; name?: string; symbol?: string; error?: string };
          try {
            ev = JSON.parse(line.slice(5).trim());
          } catch {
            continue;
          }
          if (ev.type === 'delta' && ev.text) {
            streamed = true;
            updateLast((tn) => ({ ...tn, content: tn.content + ev.text }));
          } else if (ev.type === 'tool') {
            updateLast((tn) => ({
              ...tn,
              tools: [...(tn.tools ?? []), { name: ev.name ?? '', input: { symbol: ev.symbol ?? '' } }],
            }));
          } else if (ev.type === 'error') {
            setError(t('genericError'));
          }
          requestAnimationFrame(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }));
        }
      }
      if (!streamed) {
        // Stream ended without any text (error event or empty reply): don't
        // leave a forever-empty assistant bubble — roll back and restore input.
        setTurns((prev) => prev.slice(0, -2));
        setInput(q);
        setError((prev) => prev || t('genericError'));
      }
      refreshUsage();
    } catch (e) {
      if (!streamed) {
        setTurns((prev) => prev.slice(0, -2));
        setInput(q);
      }
      setError(e instanceof ApiError ? statusError(e.status) : e instanceof Error ? e.message : 'error');
    } finally {
      setLoading(false);
      requestAnimationFrame(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }));
    }
  }

  const remaining = usage ? usage.remaining.chat ?? 0 : null;

  return (
    <div className="flex flex-col">
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-brand/10 text-brand">
          <Sparkles size={18} />
        </span>
        <div className="flex-1">
          <h1 className="text-lg font-bold leading-tight">{t('title')}</h1>
          <p className="text-xs text-zinc-500">{t('subtitle')}</p>
        </div>
        {turns.length > 0 && (
          <button
            onClick={clearChat}
            className="rounded-lg px-2 py-1 text-xs text-zinc-400 active:bg-zinc-100 dark:active:bg-zinc-800"
          >
            清除對話
          </button>
        )}
        {authed && usage && (
          <span className="rounded-full bg-zinc-100 px-2.5 py-1 text-[11px] font-medium text-zinc-500 dark:bg-zinc-800">
            {t('remaining', { n: remaining ?? 0 })}
          </span>
        )}
      </div>

      {authed === false ? (
        <AuthPanel
          locale={locale}
          onAuthed={() => {
            setAuthed(true);
            setError('');
            refreshUsage();
          }}
          ta={ta}
          needLogin={t('needLogin')}
        />
      ) : authed === true ? (
        <>
          {turns.length === 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {(['suggest1', 'suggest2', 'suggest3'] as const).map((k) => (
                <button
                  key={k}
                  onClick={() => send(t(k))}
                  className="rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs text-zinc-600 active:scale-95 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
                >
                  {t(k)}
                </button>
              ))}
            </div>
          )}

          <div className="space-y-3">
            {turns.map((tn, i) => (
              <div key={i} className={tn.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    tn.role === 'user'
                      ? 'bg-brand text-white'
                      : 'border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900'
                  }`}
                >
                  {tn.role === 'assistant' && tn.tools && tn.tools.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-1">
                      {tn.tools.map((tc, j) => (
                        <span
                          key={j}
                          className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] text-zinc-500 dark:bg-zinc-800"
                        >
                          <Wrench size={9} />
                          {toolLabel[tc.name] ?? tc.name}
                          {typeof tc.input?.symbol === 'string' ? ` · ${tc.input.symbol}` : ''}
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="whitespace-pre-wrap">
                    {tn.content || (tn.role === 'assistant' ? <span className="text-zinc-400">{t('thinking')}</span> : '')}
                  </p>
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          {quota ? (
            <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3 text-center dark:border-amber-800/50 dark:bg-amber-950/30">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                {t('quotaTitle', { n: usage?.daily_questions ?? 3 })}
              </p>
              <p className="mt-0.5 text-xs text-amber-700/90 dark:text-amber-300/80">{t('quotaHint')}</p>
              <Link
                href="/membership"
                className="mt-2 inline-block rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white"
              >
                {t('quotaCta')}
              </Link>
            </div>
          ) : error ? (
            <p className="mt-3 text-center text-xs text-amber-600">{error}</p>
          ) : null}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
            className="sticky bottom-20 mt-4 flex gap-2 rounded-2xl border border-zinc-200 bg-white p-2 shadow-lg dark:border-zinc-800 dark:bg-zinc-900"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('placeholder')}
              className="flex-1 bg-transparent px-2 text-sm outline-none"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand text-white disabled:opacity-40"
            >
              <Send size={16} />
            </button>
          </form>
        </>
      ) : (
        <div className="py-12 text-center text-sm text-zinc-400">載入中…</div>
      )}
    </div>
  );
}

function AuthPanel({
  locale,
  onAuthed,
  ta,
  needLogin,
}: {
  locale: string;
  onAuthed: () => void;
  ta: ReturnType<typeof useTranslations>;
  needLogin: string;
}) {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  // Pre-filled owner test account when built with NEXT_PUBLIC_DEV_LOGIN=1.
  const dev = process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_DEV_LOGIN === '1';
  const [email, setEmail] = useState(dev ? 'test1@wow.tw' : '');
  const [password, setPassword] = useState(dev ? 'wow123456' : '');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr('');
    try {
      if (mode === 'register') {
        await api.register({ email, password, display_name: displayName, locale });
      } else {
        await api.login({ email, password });
      }
      onAuthed();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.code : 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
      <div className="mb-3 flex items-center gap-2 text-sm font-medium text-zinc-600 dark:text-zinc-300">
        <Lock size={15} /> {needLogin}
      </div>
      <form onSubmit={submit} className="space-y-2.5">
        {mode === 'register' && (
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={ta('displayName')}
            className="w-full rounded-xl border border-zinc-300 bg-transparent px-3 py-2 text-sm dark:border-zinc-700"
          />
        )}
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={ta('email')}
          className="w-full rounded-xl border border-zinc-300 bg-transparent px-3 py-2 text-sm dark:border-zinc-700"
        />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={ta('password')}
          className="w-full rounded-xl border border-zinc-300 bg-transparent px-3 py-2 text-sm dark:border-zinc-700"
        />
        {err && <p className="text-xs text-rose-500">{err}</p>}
        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-xl bg-brand py-2.5 text-sm font-medium text-white disabled:opacity-50"
        >
          {busy ? '…' : mode === 'login' ? ta('login') : ta('register')}
        </button>
      </form>
      <button
        onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
        className="mt-3 w-full text-center text-xs text-zinc-500"
      >
        {mode === 'login' ? ta('needAccount') : ta('haveAccount')}
      </button>
    </div>
  );
}
